import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import TransitionGroup from '../TransitionGroup/TransitionGroup';

/**
 * `Snackbar` is a toolbar with actions that can be performed on the current selection as well as the number of selected
 * items.
 *
 * Containers using this component should set the *visible* prop to `true` whenever one or more items are selected, and
 * `false` when the selection is cleared. Visibility changes to the `Snackbar` will be animated.
 *
 * `Snackbar` items can be created as direct children of the snackbar` component or you may use the *onCreateMenuItems*
 * callback to lazily create the items. The items will only be rendered when the snackbar is visible, but when the items are
 * passed as children React will still spend time on creating the items. *onCreateMenuItems* prevents that as the items will
 * only be created when the snackbar is visible.
 *
 * When used in narrow containers you should set the class "vertical" to make the selection info appear on top of the
 * actions to make it more compact.
 *
 * @property {node} children The content of your snackbar. Can be any component, but `Snackbar` has styling for
 * {@link Button}, {@link Menu} and `a href` out of the box.
 * @property {string} className The class name of your snackbar. Will be appended to "core-snackbar". Add the "vertical"
 * class name when your snackbar is used inside narrow containers to apply to make it more compact.
 * @property {*} data A data attribute that will be passed to *onCreateItems* when lazily creating snackbar items.
 * @property {boolean} disabled If set to `true`, the snackbar will be disabled. Defaults to `false`.
 * @property {function} onCreateItems Snackbar items can be created lazily using this function. The item or items returned
 * from this function will be displayed in the snackbar.
 * @property {number} selectionCount The number of selected items. No selection count will be shown this prop is not
 * provided. Defaults to 0.
 * @property {string} selectionText The description of the operation that best describes the selection. Defaults to
 * "selected".
 * @property {string} selectionType The type of objects that were selected. You should change this depending on the number
 * of selected items e.g. displaying "image" when only one image is selected and "images" otherwise. Defaults to "items".
 * @property {boolean} visible If set to `true`, the snackbar will be visible. Defaults to `false`.
 *
 * @see Button, List, Menu, Table, Tree
 */
export default class Snackbar extends PureComponent {

  static displayName = 'Snackbar';

  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    data: PropTypes.any, // eslint-disable-line
    disabled: PropTypes.bool,
    onCreateItems: PropTypes.func,
    selectionCount: PropTypes.number,
    selectionText: PropTypes.string,
    selectionType: PropTypes.string,
    visible: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    selectionText: 'selected',
    selectionType: 'items',
    selectionCount: 0,
  };

  state = {};

  contentRenderer = () => {
    const { className, children, data, disabled, onCreateItems, selectionCount, selectionText, selectionType, visible, ...rest } = this.props;

    return (
      <div className={cx('core-snackbar ', className, { disabled })} {...rest}>
        <div className="snackbar-content">
          {!!selectionCount && (
            <div className="selection-info">
              <span className="count">{selectionCount}</span>
              {selectionType}&nbsp;
              {selectionText}
            </div>
          )}
          <div className="selection-actions">
            {children || (onCreateItems && onCreateItems(data))}
          </div>
        </div>
      </div>
    );
  };

  render () {
    const { className, children, data, disabled, onCreateItems, selectionCount, selectionText, selectionType, visible, ...rest } = this.props;

    return (
      <TransitionGroup
        className="animate-snackbar"
        contentRenderer={this.contentRenderer}
        in={visible}
        timeout={400}>
      </TransitionGroup>
    );
  }

}
